import type { PageType } from '@app-types/PageType'
import Button from '@components/Button/Button'
import { useLocalStorage } from '@hooks/useLocalStorage'
import type { FC } from 'react'

export type DuaCompletionSectionProps = {
  type: PageType
  page: number
  maxPage: number
  lastReadPage: number
  onGoToFirstPage: () => void
}

/**
 * Bottom summary and completion tracker for dua reading pages.
 * Displays reading progress and allows incrementing the completion counter on the last page.
 */
const DuaCompletionSection: FC<DuaCompletionSectionProps> = ({
  type,
  page,
  maxPage,
  lastReadPage,
  onGoToFirstPage,
}) => {
  const [completionCount, setCompletionCount] = useLocalStorage(
    `${type}Counter`,
    0,
  )

  const isLastPage = Number(page) === maxPage
  const difference = Number(page) - Number(lastReadPage)

  return (
    <section
      data-reading-page-end="true"
      className="flex flex-col items-center justify-center my-2 text-center text-slate-500 dark:text-slate-400 text-xs"
    >
      {isLastPage && (
        <div className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-white dark:bg-[#13222e] border border-slate-200 dark:border-[#1e3445]/60 shadow-sm max-w-sm w-full mb-4">
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            Dua tamamlandı! Tebrikler.
          </p>
          <div className="flex items-center gap-2">
            <Button
              onClick={onGoToFirstPage}
              className="py-1.5 px-3 text-xs"
            >
              Başa Dön
            </Button>
            <Button
              className="py-1.5 px-3 text-xs whitespace-nowrap"
              onDoubleClick={() => setCompletionCount(0)}
              onClick={() => setCompletionCount(completionCount + 1)}
              title="Bir arttırmak için tıklayın, sıfırlamak için çift tıklayın"
            >
              {completionCount} defa bitirildi
            </Button>
          </div>
          <p className="text-[11px] text-slate-400">
            Tıklayarak sayacı arttırabilir, çift tıklayarak sıfırlayabilirsiniz.
          </p>
        </div>
      )}

      {(Boolean(difference) || Boolean(completionCount)) && (
        <div className="py-2 px-3 rounded-full bg-slate-100 dark:bg-[#0f1d28] border border-slate-200 dark:border-slate-800">
          <span>
            {difference > 0 ? `${difference} sayfa okundu` : ''}
            {difference > 0 && completionCount > 0 ? ' • ' : ''}
            {completionCount > 0 ? `${completionCount} defa bitirildi` : ''}
          </span>
        </div>
      )}
    </section>
  )
}

export default DuaCompletionSection
